import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { KeyRound, Loader2, Mail, Link as LinkIcon } from "lucide-react";
import {
  fetchConfig,
  requestRecoveryEmail,
  saveEntitlement,
} from "../lib/pro";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function RestoreProDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("link"); // "link" | "email"
  const [linkInput, setLinkInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailDone, setEmailDone] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchConfig()
      .then((cfg) => {
        if (cancelled) return;
        setEmailEnabled(cfg.emailEnabled);
        if (!cfg.emailEnabled) setTab("link");
      })
      .catch(() => !cancelled && setEmailEnabled(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  function extractToken(raw) {
    const v = (raw || "").trim();
    if (!v) return "";
    if (v.includes("/pay/restore")) {
      try {
        const u = new URL(v);
        return u.searchParams.get("token") || "";
      } catch {
        const m = v.match(/[?&]token=([^&]+)/);
        return m ? decodeURIComponent(m[1]) : "";
      }
    }
    return v;
  }

  async function handleLink(e) {
    e.preventDefault();
    const token = extractToken(linkInput);
    if (!token) {
      toast.error("Paste your restore link or token.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get(`/payments/verify/${token}`);
      if (data?.active) {
        saveEntitlement({ ...data, token });
        toast.success("Pro Pass restored on this device.");
        setOpen(false);
        // soft-reload so Report/etc pick up the entitlement
        setTimeout(() => window.location.reload(), 400);
      } else if (data?.reason === "expired") {
        toast.error("This pass has expired.");
      } else {
        toast.error("We couldn't find that pass.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't verify the link.");
    } finally {
      setBusy(false);
    }
  }

  async function handleEmail(e) {
    e.preventDefault();
    if (!emailInput) return;
    setBusy(true);
    try {
      const res = await requestRecoveryEmail({ email: emailInput });
      setEmailDone(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not process recovery.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="bg-[#F7F5F0] border-[#E5E0D8] max-w-md"
        data-testid="restore-pro-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#1F382E] tracking-tight flex items-center gap-2">
            <KeyRound size={18} strokeWidth={1.5} className="text-[#C26D5C]" />
            Restore Pro Pass
          </DialogTitle>
          <DialogDescription className="text-[#5C665D] leading-relaxed">
            {emailEnabled
              ? "Got a Pro Pass on another device? Paste the restore link, or have us email it to you again."
              : "Got a Pro Pass on another device? Paste your restore link below."}
          </DialogDescription>
        </DialogHeader>

        <div
          className="inline-flex p-1 bg-white border border-[#E5E0D8] rounded-md w-full"
          role="tablist"
          data-testid="restore-tabs"
        >
          <button
            type="button"
            onClick={() => setTab("link")}
            className={`flex-1 px-3 py-1.5 rounded-sm text-xs transition-all flex items-center justify-center gap-1.5 ${
              tab === "link"
                ? "bg-[#1F382E] text-[#F7F5F0]"
                : "text-[#5C665D] hover:text-[#1F382E]"
            }`}
            data-testid="restore-tab-link"
          >
            <LinkIcon size={11} strokeWidth={1.5} />
            I have a link
          </button>
          {emailEnabled && (
            <button
              type="button"
              onClick={() => setTab("email")}
              className={`flex-1 px-3 py-1.5 rounded-sm text-xs transition-all flex items-center justify-center gap-1.5 ${
                tab === "email"
                  ? "bg-[#1F382E] text-[#F7F5F0]"
                  : "text-[#5C665D] hover:text-[#1F382E]"
              }`}
              data-testid="restore-tab-email"
            >
              <Mail size={11} strokeWidth={1.5} />
              Email me
            </button>
          )}
        </div>

        {tab === "link" && (
          <form onSubmit={handleLink} className="space-y-3" data-testid="restore-link-form">
            <Input
              autoFocus
              type="text"
              placeholder="https://…/pay/restore?token=… or just the token"
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              className="bg-white border-[#E5E0D8] text-sm"
              data-testid="restore-link-input"
            />
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] transition-all duration-300"
              data-testid="restore-link-submit"
            >
              {busy ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={14} />
                  Verifying…
                </>
              ) : (
                "Restore on this device"
              )}
            </Button>
          </form>
        )}

        {tab === "email" && emailEnabled && (
          <form onSubmit={handleEmail} className="space-y-3" data-testid="restore-email-form">
            <Input
              type="email"
              required
              placeholder="you@yourdomain.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="bg-white border-[#E5E0D8] text-sm"
              data-testid="restore-email-input"
            />
            <Button
              type="submit"
              disabled={busy || !!emailDone}
              className="w-full bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] transition-all duration-300"
              data-testid="restore-email-submit"
            >
              {busy ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={14} />
                  Looking up…
                </>
              ) : (
                "Send my restore link"
              )}
            </Button>
            {emailDone && (
              <p
                className="text-xs text-[#5C665D] leading-relaxed bg-white border border-[#E5E0D8] rounded-md p-3"
                data-testid="restore-email-result"
              >
                {emailDone.message}
              </p>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
